/**
 * JournalArticlesSidebar Component
 *
 * A collapsible left sidebar for the journal articles page with dynamic
 * Collections (fetched from the API) and static Topics. Exactly one option
 * is always selected, defaulting to "All Journal Articles".
 *
 * Uses the generic SecondarySideBar for the open/close toggle and layout.
 */

import { useState, useEffect, useRef } from "react"
import {
  Library,
  Tag,
  ChevronDown,
  FileClock,
  Book,
  BookMarked,
  BookCopy,
  TestTubeDiagonal,
  Microscope,
  ShieldPlus,
  TimerReset,
  Mail,
  FileText,
  ListChecks,
  Plus,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import { Popover as PopoverPrimitive } from "radix-ui"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import {
  BiblioCollection,
  PR_LITERATURE_REVIEW_TYPE,
  ROUTINE_SURVEILLANCE_TYPE,
  USER_COLLECTION_TYPE,
  createCollection,
} from "@/services/biblioService"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { useAuth, isUserInMapAppsAdmins } from "@/hooks/useAuth"
import { useJournalArticles } from "@/components/journal-articles/JournalArticlesContext"

/**
 * All possible sidebar option identifiers.
 * Collection options use the pattern "collection-{id}" with a numeric ID.
 */
export type SidebarOption =
  | "recently-added"
  | "all-journal-articles"
  | "open-author-contacts"
  | "pr-bulk-review"
  | "your-saved-articles"
  | "duplicate-articles"
  | `collection-${number}`
  | "topic-parasite-rate"
  | "pr-lit-review-overview"
  | "topic-parasite-incidence"
  | "topic-insecticide-resistance"
  | "topic-seasonality"

/** The default sidebar selection when no other option has been chosen. */
export const DEFAULT_SIDEBAR_OPTION: SidebarOption = "all-journal-articles"

/**
 * Pattern for a collection sidebar option ("collection-{id}"), capturing the
 * numeric id. Single source of truth for the collection-option encoding so the
 * convention is not re-spelled across the codebase.
 */
const COLLECTION_OPTION_RE = /^collection-(\d+)$/

/**
 * Extract the numeric collection id from a collection sidebar option.
 *
 * @param option - The sidebar option to inspect (e.g. "collection-42").
 * @returns The collection's numeric id, or null if the option is not a
 *   collection tab.
 */
export function collectionIdFromOption(option: SidebarOption): number | null {
  const match = option.match(COLLECTION_OPTION_RE)
  return match ? parseInt(match[1], 10) : null
}

/** Tooltip message shown on disabled sidebar items. */
const DISABLED_TOOLTIP_MESSAGE = "These features coming soon!"


const OPTION_META: Partial<Record<SidebarOption, { label: string; icon: React.ReactNode }>> = {
  "recently-added": { label: "Recently Added", icon: <FileClock className="size-4" /> },
  "all-journal-articles": { label: "All Journal Articles", icon: <Book className="size-4" /> },
  "open-author-contacts": {
    label: "Open Author Contacts",
    icon: <Mail className="size-4" />,
  },
  "pr-bulk-review": {
    label: "PR Bulk Review",
    icon: <ListChecks className="size-4" />,
  },
  "your-saved-articles": { label: "Your Saved Articles", icon: <BookMarked className="size-4" /> },
  "duplicate-articles": { label: "Duplicate Articles", icon: <BookCopy className="size-4" /> },
  "topic-parasite-rate": { label: "Parasite Rate", icon: <TestTubeDiagonal className="size-4" /> },
  "pr-lit-review-overview": {
    label: "PR Lit Review Overview",
    icon: <FileText className="size-4" />,
  },
  "topic-parasite-incidence": {
    label: "Parasite Incidence",
    icon: <Microscope className="size-4" />,
  },
  "topic-insecticide-resistance": {
    label: "Insecticide Resistance",
    icon: <ShieldPlus className="size-4" />,
  },
  "topic-seasonality": { label: "Seasonality", icon: <TimerReset className="size-4" /> },
}

/**
 * Returns the human-readable label for a static (non-collection) sidebar option.
 *
 * Collection options (`collection-{id}`) are intentionally not covered here,
 * since their label is the dynamic collection name — resolve those from the
 * loaded collections list instead.
 *
 * @param option - The sidebar option to look up.
 * @returns The option's display label, or undefined if it has no static label
 *   (e.g. a collection option).
 */
export function getSidebarOptionLabel(option: SidebarOption): string | undefined {
  return OPTION_META[option]?.label
}

interface JournalArticlesSidebarProps {
  selectedOption: SidebarOption
  onOptionChange: (option: SidebarOption) => void
  collections: BiblioCollection[]
  collectionsLoading: boolean
  defaultOpen?: boolean
  /** Controlled mode: whether the sidebar is expanded. */
  isOpen?: boolean
  /** Controlled mode: callback to toggle the sidebar's expanded state. */
  onToggle?: () => void
  /**
   * Increment this counter from a parent to imperatively open the "New
   * Collection" dialog (and expand the sidebar). Each distinct value triggers
   * the dialog once. Use this to let other UI (e.g. the Manage Collections
   * dialog's empty state) route the user into the create-collection flow.
   */
  openNewCollectionSignal?: number
}

/**
 * Collapsible sidebar for the journal articles page.
 *
 * Displays dynamic collections (from the API) and static topic groups.
 * Exactly one option is always selected (highlighted).
 *
 * @param props - Component props.
 * @param props.selectedOption - The currently active sidebar option.
 * @param props.onOptionChange - Handler called when a new option is selected.
 * @param props.collections - Collections fetched from the API.
 * @param props.collectionsLoading - Whether collections are still loading.
 * @returns The rendered sidebar element.
 */
export default function JournalArticlesSidebar({
  selectedOption,
  onOptionChange,
  collections,
  collectionsLoading,
  defaultOpen = true,
  isOpen,
  onToggle,
  openNewCollectionSignal,
}: JournalArticlesSidebarProps): React.JSX.Element {
  // Auth token + current user (for the creator's sub), and the shared
  // collections refetch so a newly created collection shows up without reload.
  const { getAccessToken, user } = useAuth()
  const userIsAdmin = isUserInMapAppsAdmins(user)

  const { fetchCollections } = useJournalArticles()

  const [collectionsOpen, setCollectionsOpen] = useState<boolean>(true)
  const [topicsOpen, setTopicsOpen] = useState<boolean>(true)
  const [parasiteRateOpen, setParasiteRateOpen] = useState<boolean>(false)
  const [parasiteIncidenceOpen, setParasiteIncidenceOpen] = useState<boolean>(false)

  // Controls the "New Collection" dialog visibility and the in-progress name
  // the user is typing.
  const [newCollectionDialogOpen, setNewCollectionDialogOpen] = useState<boolean>(false)
  const [newCollectionName, setNewCollectionName] = useState<string>("")
  // Whether a create request is in flight, and the last create error (if any),
  // shown inline in the dialog.
  const [creatingCollection, setCreatingCollection] = useState<boolean>(false)
  const [createCollectionError, setCreateCollectionError] = useState<string | null>(null)

  // Collections whose type maps them under the "Parasite Rate" topic.
  const prCollections: BiblioCollection[] = collections.filter(
    collection => collection.collectionType === PR_LITERATURE_REVIEW_TYPE
  )

  // Collections whose type maps them under the "Parasite Incidence" topic.
  const apiCollections: BiblioCollection[] = collections.filter(
    collection => collection.collectionType === ROUTINE_SURVEILLANCE_TYPE
  )

  // Remaining collections that are not nested under a topic. These are the only
  // collections shown in the flat "Collections" group.
  const otherCollections: BiblioCollection[] = collections.filter(
    collection =>
      collection.collectionType !== PR_LITERATURE_REVIEW_TYPE &&
      collection.collectionType !== ROUTINE_SURVEILLANCE_TYPE
  )

  // When the selected option is a collection nested under a topic (e.g. after a
  // page refresh while viewing a PR collection), auto-expand that topic group so
  // the selected collection is visible. This runs once collections have loaded,
  // since on refresh the collections list is initially empty. We only open the
  // matching group (never force it closed), so a user's manual collapse of the
  // other groups is preserved.
  useEffect(() => {
    // The "PR Lit Review Overview" tab lives under Parasite Rate, so expand
    // that group when it is the selected option (e.g. on page refresh).
    if (selectedOption === "pr-lit-review-overview") {
      setParasiteRateOpen(true)
      return
    }

    const selectedCollectionId: number | null = collectionIdFromOption(selectedOption)
    if (selectedCollectionId === null) {
      return
    }

    const selectedCollection: BiblioCollection | undefined = collections.find(
      collection => collection.id === selectedCollectionId
    )
    if (selectedCollection === undefined) {
      return
    }

    if (selectedCollection.collectionType === PR_LITERATURE_REVIEW_TYPE) {
      setParasiteRateOpen(true)
    } else if (selectedCollection.collectionType === ROUTINE_SURVEILLANCE_TYPE) {
      setParasiteIncidenceOpen(true)
    }
  }, [selectedOption, collections])

  /**
   * Sorts a list of collections alphabetically by name without mutating the input.
   *
   * @param toSort - The collections to sort.
   * @returns A new array sorted by `collectionName` ascending.
   */
  const sortByName = (toSort: BiblioCollection[]): BiblioCollection[] =>
    [...toSort].sort((a, b) => a.collectionName.localeCompare(b.collectionName))

  /**
   * Opens the "New Collection" dialog with a cleared name field and no prior
   * error.
   *
   * @returns Nothing; updates local dialog state.
   */
  const openNewCollectionDialog = (): void => {
    setNewCollectionName("")
    setCreateCollectionError(null)
    setNewCollectionDialogOpen(true)
  }

  const lastHandledCreateSignalRef = useRef<number | undefined>(openNewCollectionSignal)
  useEffect(() => {
    if (openNewCollectionSignal === undefined) {
      return
    }
    if (openNewCollectionSignal === lastHandledCreateSignalRef.current) {
      return
    }
    lastHandledCreateSignalRef.current = openNewCollectionSignal
    openNewCollectionDialog()
  }, [openNewCollectionSignal])

  /**
   * Confirms creation of a new collection from the dialog.
   *
   * @returns A promise that resolves once the create attempt has completed.
   */
  const handleCreateCollection = async (): Promise<void> => {
    const trimmedName: string = newCollectionName.trim()
    if (trimmedName.length === 0 || creatingCollection) {
      return
    }

    // The collection is made private to its creator by seeding "members" with
    // the creator's Auth0 "sub". Fail loudly if the sub is missing rather than
    // silently creating a public collection (empty members = visible to all).
    const currentUserSub: string = user?.sub ?? ""
    if (currentUserSub.length === 0) {
      setCreateCollectionError(
        "Could not determine your user identity. Please sign in again and retry."
      )
      return
    }

    setCreatingCollection(true)
    setCreateCollectionError(null)

    try {
      const token: string = await getAccessToken()
      await createCollection(token, {
        collectionName: trimmedName,
        collectionType: USER_COLLECTION_TYPE,
        members: [currentUserSub],
      })
      // Refresh the shared collections list so the new collection appears in
      // the sidebar, then close and reset the dialog.
      await fetchCollections()
      setNewCollectionDialogOpen(false)
      setNewCollectionName("")
    } catch (err) {
      // Surface the failure inline and keep the dialog open so the user can
      // retry without re-typing the name.
      const message: string =
        err instanceof Error ? err.message : "Failed to create collection"
      setCreateCollectionError(message)
    } finally {
      setCreatingCollection(false)
    }
  }

  /**
   * Renders the "+" icon button used to open the New Collection dialog.
   *
   * Shared between the expanded "My Collections" header and the collapsed-rail
   * popover header so both entry points look and behave identically.
   *
   * @returns The rendered create-collection trigger button.
   */
  const renderNewCollectionButton = (): React.JSX.Element => {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={openNewCollectionDialog}
            aria-label="New collection"
            className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <Plus className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">New collection</TooltipContent>
      </Tooltip>
    )
  }

  /**
   * Returns true if the given option is currently disabled (not yet implemented).
   * @param option - The sidebar option to check.
   */
  const isOptionDisabled = (option: SidebarOption): boolean => {
    if (option === "all-journal-articles") return false
    if (option === "recently-added") return false
    if (option === "open-author-contacts") return false
    if (option === "pr-bulk-review") return false
    if (option === "pr-lit-review-overview") return false
    if (option.startsWith("topic-")) return false
    if (option.startsWith("collection-")) return false
    return true
  }

  const getButtonClassName = (option: SidebarOption): string =>
    cn(
      "text-left text-xs px-2 py-1 rounded-md transition-colors w-full",
      isOptionDisabled(option)
        ? "text-muted-foreground/50 cursor-not-allowed"
        : option === selectedOption
          ? "bg-accent text-accent-foreground font-medium"
          : "hover:bg-muted"
    )

  /**
   * Renders a sidebar button, optionally wrapped in a tooltip if the option is disabled.
   *
   * @param option - The sidebar option identifier.
   * @param label - The display label for the button.
   */
  const renderSidebarButton = (option: SidebarOption, label: string): React.JSX.Element => {
    const disabled = isOptionDisabled(option)
    const icon = OPTION_META[option]?.icon ?? null

    const button = (
      <button
        type="button"
        className={getButtonClassName(option)}
        onClick={() => onOptionChange(option)}
        disabled={disabled}
      >
        <span className="flex items-center gap-2">
          {icon}
          {label}
        </span>
      </button>
    )

    if (disabled) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-full">{button}</span>
          </TooltipTrigger>
          <TooltipContent side="right">{DISABLED_TOOLTIP_MESSAGE}</TooltipContent>
        </Tooltip>
      )
    }

    return button
  }

  /**
   * Renders a topic row that is both a selectable filter and an expand/collapse
   * toggle for a set of nested collections. Clicking the label selects the topic
   * (existing navigation behavior); clicking the chevron expands the nested
   * collections. When the topic has no matching collections the chevron is
   * hidden and the row behaves as a plain selectable topic.
   *
   * @param option - The topic's sidebar option identifier.
   * @param label - The topic's display label.
   * @param nestedCollections - Collections to nest under this topic.
   * @param open - Whether the nested section is currently expanded.
   * @param onOpenChange - Handler to toggle the expanded state.
   * @param leadingItems - Optional static nested items rendered above the
   *   collections (e.g. the "PR Lit Review Overview" tab under Parasite Rate).
   * @returns The rendered expandable topic row.
   */
  const renderExpandableTopic = (
    option: SidebarOption,
    label: string,
    nestedCollections: BiblioCollection[],
    open: boolean,
    onOpenChange: (open: boolean) => void,
    leadingItems?: React.ReactNode
  ): React.JSX.Element => {
    // The group is expandable when it has either static leading items or
    // matching collections to show.
    const hasNested = leadingItems !== undefined || nestedCollections.length > 0

    return (
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <div className="flex items-center gap-0.5">
          {/* Label selects the topic filter (unchanged navigation behavior). */}
          <span className="flex-1 min-w-0">{renderSidebarButton(option, label)}</span>
          {/* Separate chevron toggles the nested content without navigating. */}
          {hasNested && (
            <CollapsibleTrigger
              className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
            >
              <ChevronDown
                className={cn(
                  "size-3.5 transition-transform duration-200",
                  open ? "rotate-0" : "-rotate-90"
                )}
              />
            </CollapsibleTrigger>
          )}
        </div>
        {hasNested && (
          <CollapsibleContent>
            <div className="flex flex-col gap-0.5 py-0.5 pl-4">
              {leadingItems}
              {sortByName(nestedCollections).map(collection => (
                <span key={collection.id}>
                  {renderSidebarButton(`collection-${collection.id}`, collection.collectionName)}
                </span>
              ))}
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>
    )
  }


  const renderCollapsedButton = (option: SidebarOption): React.JSX.Element => {
    const disabled = isOptionDisabled(option)
    const meta = OPTION_META[option]!

    return (
      <Tooltip key={option}>
        <TooltipTrigger asChild>
          <span className="w-full flex justify-center">
            <button
              type="button"
              onClick={() => !disabled && onOptionChange(option)}
              disabled={disabled}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded transition-colors",
                disabled
                  ? "opacity-40 cursor-not-allowed"
                  : option === selectedOption
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-muted cursor-pointer"
              )}
            >
              {meta.icon}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{meta.label}</TooltipContent>
      </Tooltip>
    )
  }

  /**
   * Renders a single topic entry inside the collapsed-view Topics popover.
   *
   * @param option - The topic's sidebar option identifier.
   * @returns The rendered topic button.
   */
  const renderCollapsedTopicButton = (option: SidebarOption): React.JSX.Element => {
    const meta = OPTION_META[option]!
    return (
      <button
        key={option}
        type="button"
        onClick={() => onOptionChange(option)}
        className={cn(
          "flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded transition-colors",
          option === selectedOption
            ? "bg-accent text-accent-foreground font-medium"
            : "hover:bg-accent"
        )}
      >
        {meta.icon}
        <span className="truncate">{meta.label}</span>
      </button>
    )
  }

  /**
   * Renders a topic entry in the collapsed-view Topics popover along with its
   * nested collections indented beneath it. The topic itself remains a
   * selectable filter; each nested collection is independently selectable.
   *
   * @param option - The topic's sidebar option identifier.
   * @param nestedCollections - Collections to list beneath the topic.
   * @param leadingItems - Optional static nested items rendered above the
   *   collections (e.g. the "PR Lit Review Overview" tab under Parasite Rate).
   * @returns The rendered topic group.
   */
  const renderCollapsedTopicWithCollections = (
    option: SidebarOption,
    nestedCollections: BiblioCollection[],
    leadingItems?: React.ReactNode
  ): React.JSX.Element => {
    const hasNested = leadingItems !== undefined || nestedCollections.length > 0
    return (
      <div key={option}>
        {renderCollapsedTopicButton(option)}
        {hasNested && (
          <div className="flex flex-col gap-0.5 pl-4">
            {leadingItems}
            {sortByName(nestedCollections).map(collection => {
              const collectionOption: SidebarOption = `collection-${collection.id}`
              return (
                <button
                  key={collection.id}
                  type="button"
                  onClick={() => onOptionChange(collectionOption)}
                  className={cn(
                    "flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded transition-colors",
                    collectionOption === selectedOption
                      ? "bg-accent text-accent-foreground font-medium"
                      : "hover:bg-accent"
                  )}
                >
                  <Book className="size-3.5 shrink-0" />
                  <span className="truncate">{collection.collectionName}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <SecondarySideBar
      defaultOpen={defaultOpen}
      isOpen={isOpen}
      onToggle={onToggle}
      collapsedContent={
        <>
          {renderCollapsedButton("recently-added")}
          {renderCollapsedButton("all-journal-articles")}
          {renderCollapsedButton("open-author-contacts")}
          {renderCollapsedButton("pr-bulk-review")}
          {renderCollapsedButton("your-saved-articles")}
          {renderCollapsedButton("duplicate-articles")}

          {/* Topics group — popover with all topic icons */}
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded hover:bg-muted cursor-pointer transition-colors mx-auto"
                  >
                    <Tag className="size-4" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">Topics</TooltipContent>
            </Tooltip>
            <PopoverContent side="right" align="start" className="w-52 gap-0 p-1">
              <PopoverPrimitive.Arrow
                width={12}
                height={6}
                className="fill-popover stroke-(--color-foreground)/12 stroke-[3px] [paint-order:stroke_fill]"
              />
              <p className="px-2 py-1 text-xs font-semibold text-muted-foreground">Topics</p>
              {renderCollapsedTopicWithCollections(
                "topic-parasite-rate",
                prCollections,
                renderCollapsedTopicButton("pr-lit-review-overview")
              )}
              {renderCollapsedTopicWithCollections("topic-parasite-incidence", apiCollections)}
              {(
                ["topic-insecticide-resistance", "topic-seasonality"] as SidebarOption[]
              ).map(option => renderCollapsedTopicButton(option))}
            </PopoverContent>
          </Popover>

          {/* My Collections group — popover with all collection icons */}
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded hover:bg-muted cursor-pointer transition-colors mx-auto"
                  >
                    <Library className="size-4" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">My Collections</TooltipContent>
            </Tooltip>
            <PopoverContent side="right" align="start" className="w-48 gap-0 p-1">
              <PopoverPrimitive.Arrow
                width={12}
                height={6}
                className="fill-popover stroke-(--color-foreground)/12 stroke-[3px] [paint-order:stroke_fill]"
              />
              <div className="flex items-center justify-between px-2 py-1">
                <p className="text-xs font-semibold text-muted-foreground">My Collections</p>
                {renderNewCollectionButton()}
              </div>
              {collectionsLoading ? (
                <LoadingSpinner variant={"default"} loadingContent={"Loading..."} />
              ) : otherCollections.length === 0 ? (
                <p className="px-2 py-1 text-xs text-muted-foreground">No collections</p>
              ) : (
                sortByName(otherCollections).map(collection => {
                  const option: SidebarOption = `collection-${collection.id}`
                  return (
                    <button
                      key={collection.id}
                      type="button"
                      onClick={() => onOptionChange(option)}
                      className={cn(
                        "flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded transition-colors",
                        option === selectedOption
                          ? "bg-accent text-accent-foreground font-medium"
                          : "hover:bg-accent"
                      )}
                    >
                      <Book className="size-3.5 shrink-0" />
                      <span className="truncate">{collection.collectionName}</span>
                    </button>
                  )
                })
              )}
            </PopoverContent>
          </Popover>
        </>
      }
    >
      <div className="flex flex-col gap-0.5">
        {renderSidebarButton("recently-added", "Recently Added")}
        {renderSidebarButton("all-journal-articles", "All Journal Articles")}
        {renderSidebarButton("open-author-contacts", "Open Author Contacts")}
        {userIsAdmin && (
          renderSidebarButton("pr-bulk-review", "PR Bulk Review")
        )}
        {renderSidebarButton("your-saved-articles", "Your Saved Articles")}
        {renderSidebarButton("duplicate-articles", "Duplicate Articles")}
      </div>

      {/* Topics group — collapsible */}
      <Collapsible open={topicsOpen} onOpenChange={setTopicsOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground rounded-md transition-colors cursor-pointer">
          <span className="flex items-center gap-2">
            <Tag className="size-4" />
            Topics
          </span>
          <ChevronDown
            className={cn(
              "size-4 transition-transform duration-200",
              topicsOpen ? "rotate-0" : "-rotate-90"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-0.5 py-1 pl-3">
            {renderExpandableTopic(
              "topic-parasite-rate",
              "Parasite Rate",
              prCollections,
              parasiteRateOpen,
              setParasiteRateOpen,
              renderSidebarButton("pr-lit-review-overview", "PR Lit Review Overview")
            )}
            {renderExpandableTopic(
              "topic-parasite-incidence",
              "Parasite Incidence",
              apiCollections,
              parasiteIncidenceOpen,
              setParasiteIncidenceOpen
            )}
            {renderSidebarButton("topic-insecticide-resistance", "Insecticide Resistance")}
            {renderSidebarButton("topic-seasonality", "Seasonality")}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* My Collections group — collapsible, dynamically populated */}
      <Collapsible open={collectionsOpen} onOpenChange={setCollectionsOpen}>
        <div className="flex items-center gap-0.5">
          {/* Label toggles the group open/closed. */}
          <CollapsibleTrigger className="flex flex-1 min-w-0 items-center px-2 py-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground rounded-md transition-colors cursor-pointer">
            <span className="flex items-center gap-2">
              <Library className="size-4 " />
              My Collections
            </span>
          </CollapsibleTrigger>
          {renderNewCollectionButton()}
          <CollapsibleTrigger
            className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            aria-label={collectionsOpen ? "Collapse My Collections" : "Expand My Collections"}
          >
            <ChevronDown
              className={cn(
                "size-4 transition-transform duration-200",
                collectionsOpen ? "rotate-0" : "-rotate-90"
              )}
            />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className="flex flex-col gap-0.5 py-1 pl-3">
            {collectionsLoading ? (
              <LoadingSpinner variant={"default"} loadingContent={"Loading..."} />
            ) : otherCollections.length === 0 ? (
              <span className="text-xs text-muted-foreground px-2 py-1">No collections</span>
            ) : (
              sortByName(otherCollections).map(collection => (
                <span key={collection.id}>
                  {renderSidebarButton(`collection-${collection.id}`, collection.collectionName)}
                </span>
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* New Collection dialog.*/}
      <Dialog
        open={newCollectionDialogOpen}
        onOpenChange={open => {
          // Prevent closing (via overlay/escape) while a create is in flight so
          // the user cannot dismiss a request mid-save.
          if (!creatingCollection) {
            setNewCollectionDialogOpen(open)
          }
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Collection</DialogTitle>
            <DialogDescription>
              Please name your collection
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            value={newCollectionName}
            onChange={event => setNewCollectionName(event.target.value)}
            placeholder="Collection name"
            aria-label="Collection name"
            disabled={creatingCollection}
            onKeyDown={event => {
              if (
                event.key === "Enter" &&
                newCollectionName.trim().length > 0 &&
                !creatingCollection
              ) {
                handleCreateCollection()
              }
            }}
          />
          {/* Inline error: shown only when a create attempt failed. */}
          {createCollectionError !== null && (
            <p className="text-xs text-destructive" role="alert">
              {createCollectionError}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewCollectionDialogOpen(false)}
              disabled={creatingCollection}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCollection}
              disabled={newCollectionName.trim().length === 0 || creatingCollection}
            >
              {creatingCollection && <Loader2 className="size-3.5 animate-spin" />}
              {creatingCollection ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SecondarySideBar>
  )
}
